import React, { useEffect, useState } from "react";
import { Download, Search } from "lucide-react";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { getAuditLog } from "../../lib/api";
import { formatDate } from "../../lib/utils";

const ACTION_VARIANT = {
  BASELINE_UPLOADED: "info",
  BASELINE_APPROVED: "success",
  BASELINE_PUBLISHED: "success",
  ROLLOUT_STARTED: "info",
  ROLLOUT_PAUSED: "warning",
  ROLLOUT_RESUMED: "info",
  USER_ROLE_ASSIGNED: "muted",
};

export default function AuditLogPage() {
  const [records, setRecords] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    getAuditLog().then(setRecords).catch(console.error);
  }, []);

  const filtered = records
    .filter(
      (r) =>
        r.action.toLowerCase().includes(filter.toLowerCase()) ||
        r.actor.toLowerCase().includes(filter.toLowerCase()) ||
        r.resource.toLowerCase().includes(filter.toLowerCase())
    )
    .sort((a, b) => new Date(b.ts) - new Date(a.ts));

  const exportCsv = () => {
    const rows = [
      ["ID", "Action", "Resource", "Actor", "Role", "Timestamp", "IP"],
      ...filtered.map((r) => [r.id, r.action, r.resource, r.actor, r.role, r.ts, r.ip]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "audit-log.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Audit Log</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Immutable control-plane events. Backed by DynamoDB AuditLog table + CloudTrail.
            Append-only — no record can be modified or deleted.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv}>
          <Download className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input className="pl-9" placeholder="Search action, actor, resource…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Timestamp</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No audit records match.</TableCell></TableRow>
              )}
              {filtered.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Badge variant={ACTION_VARIANT[r.action] || "muted"} className="text-[11px] font-mono">
                      {r.action}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{r.resource}</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.actor}</TableCell>
                  <TableCell>
                    <span className="text-xs text-slate-500">{r.role}</span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{formatDate(r.ts)}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-400">{r.ip}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
